// Field schema for the **accommodation** rate-line extraction specialist.
//
// Hard-coded source of truth for the structured payload we expect the
// accommodation specialist to emit. The Extraction modal renders directly
// from these constants; the parsing prompt and downstream consumers should
// also reference them so a single edit here propagates everywhere.
//
// Sibling files:
// - ./accommodation-parameters.js — prompts that drive the specialist
// - ./green-fees-schema.js — the parallel specialist's schema

export const ACCOMMODATION_FIELD_SCHEMA = [
	{
		key: "extraction_id",
		type: "string (UUID)",
		required: true,
		description: "Unique identifier for this extraction run. Correlates the extracted payload with the orchestrator run that produced it.",
		agent_note: "Generate a UUID v4 string. Do not reuse identifiers from previous runs. Each extraction call should be uniquely traceable for audit.",
	},
	{
		key: "extracted_at",
		type: "ISO datetime",
		required: true,
		description: "Timestamp recording when this extraction run completed.",
		agent_note: "ISO 8601 with timezone, UTC preferred (\"2026-05-13T16:42:00Z\"). Set from the system clock at output time — do not infer from any date inside the document.",
	},
	{
		key: "accommodation_lines[].room_type_label",
		type: "string",
		required: true,
		description: "Room type label exactly as printed on the rate table header. Verbatim, including casing, diacritics, and any descriptors (\"Sea View\", \"Garden\", \"Junior\").",
		agent_note: "Capture exactly as written. Do not translate, paraphrase, or normalise. \"Double Standard Sea View\" stays as \"Double Standard Sea View\".",
	},
	{
		key: "accommodation_lines[].room_category",
		type: "string (enum)",
		required: true,
		options: [
			{ value: "Single", description: "Single-occupancy room." },
			{ value: "Twin", description: "Twin-bedded room." },
			{ value: "Double", description: "Double-bedded room." },
			{ value: "Triple", description: "Triple-share room." },
			{ value: "Quad", description: "Quad-share room." },
			{ value: "Suite", description: "Suite, junior suite, executive suite — anything labelled \"suite\"." },
			{ value: "Studio", description: "Studio apartment with kitchenette." },
			{ value: "Apartment", description: "Self-catering apartment with separate bedroom(s)." },
			{ value: "Villa", description: "Standalone villa." },
			{ value: "Other", description: "Anything that doesn't fit the above." },
		],
		agent_note: "Pick the closest category. Maps the verbatim `room_type_label` onto a normalised bucket the downstream search hooks can index. \"Junior Suite\" → Suite; \"Triple-share Studio\" → Triple if the rate is per-person and the headcount is the dominant axis, else Studio.",
	},
	{
		key: "accommodation_lines[].board_basis",
		type: "string (enum) | null",
		required: false,
		options: [
			{ value: "RO", description: "Room only — no meals included." },
			{ value: "BB", description: "Bed & breakfast." },
			{ value: "HB", description: "Half board — breakfast + one other meal." },
			{ value: "FB", description: "Full board — three meals." },
			{ value: "AI", description: "All inclusive." },
			{ value: "UAI", description: "Ultra/premium all inclusive." },
		],
		agent_note: "Normalise to the enum. Localised labels (\"Media pensión\", \"Mezza pensione\", \"Alojamento e pequeno-almoço\") map to the equivalent enum value; note the original label in the line's `notes`. Null only when the document does not state a board basis.",
	},
	{
		key: "accommodation_lines[].occupancy",
		type: "number",
		required: true,
		description: "Number of adults the rate covers when the price_basis is per_person, or the implicit room capacity otherwise.",
		agent_note: "Integer count. For per-room rates, this is the implied number of adults filling the room (Double → 2, Triple → 3). For per-person rates, this is the price's pax denominator. Default to 2 for Double rooms when the document is silent.",
	},
	{
		key: "accommodation_lines[].period.start",
		type: "ISO date",
		required: true,
		description: "First date the rate applies (inclusive).",
		agent_note: "ISO YYYY-MM-DD. If the rate card uses season labels (\"Summer 2026\"), resolve to the most precise boundary the document supports and note the assumption in the line's `notes`.",
	},
	{
		key: "accommodation_lines[].period.end",
		type: "ISO date",
		required: true,
		description: "Last date the rate applies (inclusive).",
		agent_note: "ISO YYYY-MM-DD, inclusive. Same parsing rules as `period.start`.",
	},
	{
		key: "accommodation_lines[].month_set",
		type: "integer[]",
		required: true,
		description: "Denormalised calendar months (1..12) that the period touches, even partially. Used by the indexer to build the document-level `months_available[]` hook.",
		agent_note: "Integer array, each value in [1, 12], sorted ascending, no duplicates. A period 2026-04-15 → 2026-09-15 produces [4, 5, 6, 7, 8, 9].",
	},
	{
		key: "accommodation_lines[].min_stay_nights",
		type: "number | null",
		required: false,
		description: "Minimum stay nights enforced for this rate, if stated. Null when no minimum is specified.",
		agent_note: "Integer count. Capture only when the document explicitly states a minimum-stay restriction for this rate cell.",
	},
	{
		key: "accommodation_lines[].max_stay_nights",
		type: "number | null",
		required: false,
		description: "Maximum stay nights enforced for this rate, if stated.",
		agent_note: "Integer count. Almost always null; only populate when the document explicitly caps the stay length.",
	},
	{
		key: "accommodation_lines[].price_native",
		type: "number",
		required: true,
		description: "Rate value in the supplier's native currency.",
		agent_note: "Number with up to two decimal places. Do not convert currencies. Do not apply margin or markup. The price as printed on the rate table.",
	},
	{
		key: "accommodation_lines[].currency",
		type: "string (ISO 4217)",
		required: true,
		description: "ISO 4217 alpha-3 currency code (\"EUR\", \"GBP\", \"USD\").",
		agent_note: "Three-letter uppercase code. Resolve currency symbols (€ → EUR, £ → GBP, $ → USD or other; check supplier locale when ambiguous).",
	},
	{
		key: "accommodation_lines[].price_basis",
		type: "string (enum)",
		required: true,
		options: [
			{ value: "per_room_per_night", description: "Standard per-room nightly rate." },
			{ value: "per_person_per_night", description: "Per-person nightly rate (typical of villa/apartment quoting on PAX)." },
			{ value: "per_room_per_stay", description: "Flat rate for the whole stay regardless of nights." },
			{ value: "per_person_per_stay", description: "Per-person flat rate for the whole stay." },
		],
		agent_note: "Decide from the rate table header (\"per room per night\", \"per person\"). If unstated, default to `per_room_per_night` for whole-room categories (Single/Twin/Double/Triple/Quad/Suite/Studio/Apartment/Villa) and explain the assumption in the line's `notes`.",
	},
	{
		key: "accommodation_lines[].view_or_category",
		type: "string | null",
		required: false,
		description: "Sub-classifier baked into the room type label (\"Sea View\", \"Garden View\", \"Pool View\", \"Mountain View\"). Null when the label does not include one.",
		agent_note: "Capture the view/category descriptor verbatim from the label. Used as a search hook for clients who request a specific view.",
	},
	{
		key: "accommodation_lines[].notes",
		type: "string | null",
		required: false,
		description: "Anything that doesn't fit elsewhere on this line but is worth flagging for review.",
		agent_note: "Localised board-basis labels, assumptions about ambiguous date boundaries, contradictory rates spotted in cross-references. One short sentence per line.",
	},
	{
		key: "accommodation_supplements[].kind",
		type: "string (enum)",
		required: true,
		options: [
			{ value: "single_supplement", description: "Surcharge for single-person occupancy of a Double/Twin." },
			{ value: "extra_bed", description: "Rate for an extra bed/rollaway." },
			{ value: "child_rate", description: "Modifier for child pricing (often a percentage discount)." },
			{ value: "infant_rate", description: "Modifier for infant pricing (often free or fixed fee)." },
			{ value: "board_upgrade", description: "Surcharge to upgrade meal plan (BB → HB, HB → FB, etc.)." },
			{ value: "city_tax", description: "Per-night/per-person tax levied by the municipality." },
			{ value: "resort_fee", description: "Per-night/per-stay resort fee." },
			{ value: "early_check_in", description: "Surcharge for check-in before standard time." },
			{ value: "late_check_out", description: "Surcharge for check-out after standard time." },
			{ value: "other", description: "Anything that doesn't fit the above; describe in `notes`." },
		],
		agent_note: "Pick the closest kind. When uncertain, use `other` and describe the supplement in `notes` rather than misclassify.",
	},
	{
		key: "accommodation_supplements[].applies_to",
		type: "string[]",
		required: true,
		description: "Room type labels (verbatim, matching `accommodation_lines[].room_type_label`) this supplement applies to. Empty array means the supplement applies to every line on the doc.",
		agent_note: "Verbatim labels exactly as used in the lines above. Empty array for blanket supplements (e.g. a 2€ per-night city tax that applies to every room type).",
	},
	{
		key: "accommodation_supplements[].value_native",
		type: "number | null",
		required: false,
		description: "Absolute supplement amount in native currency. Null when the supplement is percentage-based.",
		agent_note: "Use when the document states a fixed amount. Mutually exclusive with `value_pct` — exactly one of the two should be populated.",
	},
	{
		key: "accommodation_supplements[].value_pct",
		type: "number | null",
		required: false,
		description: "Percentage supplement value (e.g. 50 for \"50% off\"). Null when the supplement is an absolute amount.",
		agent_note: "Number, not a fraction (50, not 0.5). For child rates expressed as \"50% off the adult rate\", populate as 50 and put the resulting direction (discount) implicitly in `kind = child_rate`. Mutually exclusive with `value_native`.",
	},
	{
		key: "accommodation_supplements[].currency",
		type: "string (ISO 4217) | null",
		required: false,
		description: "Currency for `value_native`. Null when the supplement is percentage-based.",
		agent_note: "Match the rate-card currency. Null when `value_pct` is populated instead.",
	},
	{
		key: "accommodation_supplements[].basis",
		type: "string (enum)",
		required: true,
		options: [
			{ value: "per_night", description: "Applied per night of stay." },
			{ value: "per_stay", description: "Applied once per stay regardless of length." },
			{ value: "per_person", description: "Applied per person." },
			{ value: "per_person_per_night", description: "Applied per person per night." },
			{ value: "fixed", description: "Flat amount, basis not relevant (e.g. a flat resort fee that just adds once)." },
		],
		agent_note: "Pick from the supplement's stated unit. Default to `per_night` for nightly taxes/fees, `per_stay` for one-off fees, `per_person_per_night` for board upgrades.",
	},
	{
		key: "accommodation_supplements[].period.start",
		type: "ISO date | null",
		required: false,
		description: "First date the supplement applies. Null when the supplement applies for the entire rate-card validity.",
		agent_note: "ISO YYYY-MM-DD. Use only when the supplement is restricted to a sub-period (e.g. a Christmas-week gala dinner supplement).",
	},
	{
		key: "accommodation_supplements[].period.end",
		type: "ISO date | null",
		required: false,
		description: "Last date the supplement applies (inclusive). Null when the supplement applies for the entire rate-card validity.",
		agent_note: "ISO YYYY-MM-DD, inclusive. Mirror the rules for `accommodation_lines[].period.end`.",
	},
	{
		key: "accommodation_supplements[].age_range",
		type: "string | null",
		required: false,
		description: "Age range for child/infant supplements, as stated on the document (e.g. \"2-11\", \"0-1\").",
		agent_note: "Verbatim from the document. Null for non-age-banded supplements.",
	},
	{
		key: "accommodation_supplements[].notes",
		type: "string | null",
		required: false,
		description: "Anything that doesn't fit elsewhere on this supplement.",
		agent_note: "Original localised label, ambiguous wording, qualifiers like \"per adult sharing\".",
	},
	{
		key: "agent_metadata.confidence_score",
		type: "number",
		required: true,
		description: "Calibrated self-confidence in the overall extraction, between 0 and 1.",
		agent_note: "Float in [0, 1] reflecting how confident you are the extracted lines and supplements faithfully represent the rate card. 0.9+ for clean tabular rate cards; 0.6–0.9 when some inference is required; <0.6 when significant guesswork was needed.",
	},
	{
		key: "agent_metadata.reasoning",
		type: "string",
		required: true,
		description: "Short rationale describing line/supplement counts and any material assumptions.",
		agent_note: "One short paragraph. Cover (a) how many lines and supplements you emitted and the axes you decomposed across, (b) the most material assumptions (board-basis mapping, currency resolution, ambiguous periods), and (c) anything you deliberately did not extract because it belonged to the green-fee specialist.",
	},
]

export const ACCOMMODATION_FIELD_JSON_SCHEMA_NAME = "supplier_accommodation_extraction"

export const ACCOMMODATION_FIELD_JSON_SCHEMA = {
	type: "object",
	additionalProperties: false,
	properties: {
		extraction_id: { type: "string" },
		extracted_at: { type: "string" },
		accommodation_lines: {
			type: "array",
			items: {
				type: "object",
				additionalProperties: false,
				properties: {
					room_type_label: { type: "string" },
					room_category: {
						type: "string",
						enum: [
							"Single",
							"Twin",
							"Double",
							"Triple",
							"Quad",
							"Suite",
							"Studio",
							"Apartment",
							"Villa",
							"Other",
						],
					},
					board_basis: {
						type: ["string", "null"],
						enum: ["RO", "BB", "HB", "FB", "AI", "UAI", null],
					},
					occupancy: { type: "number" },
					period: {
						type: "object",
						additionalProperties: false,
						properties: {
							start: { type: "string" },
							end: { type: "string" },
						},
						required: ["start", "end"],
					},
					month_set: {
						type: "array",
						items: { type: "number", minimum: 1, maximum: 12 },
					},
					min_stay_nights: { type: ["number", "null"] },
					max_stay_nights: { type: ["number", "null"] },
					price_native: { type: "number" },
					currency: { type: "string" },
					price_basis: {
						type: "string",
						enum: [
							"per_room_per_night",
							"per_person_per_night",
							"per_room_per_stay",
							"per_person_per_stay",
						],
					},
					view_or_category: { type: ["string", "null"] },
					notes: { type: ["string", "null"] },
				},
				required: [
					"room_type_label",
					"room_category",
					"board_basis",
					"occupancy",
					"period",
					"month_set",
					"min_stay_nights",
					"max_stay_nights",
					"price_native",
					"currency",
					"price_basis",
					"view_or_category",
					"notes",
				],
			},
		},
		accommodation_supplements: {
			type: "array",
			items: {
				type: "object",
				additionalProperties: false,
				properties: {
					kind: {
						type: "string",
						enum: [
							"single_supplement",
							"extra_bed",
							"child_rate",
							"infant_rate",
							"board_upgrade",
							"city_tax",
							"resort_fee",
							"early_check_in",
							"late_check_out",
							"other",
						],
					},
					applies_to: {
						type: "array",
						items: { type: "string" },
					},
					value_native: { type: ["number", "null"] },
					value_pct: { type: ["number", "null"] },
					currency: { type: ["string", "null"] },
					basis: {
						type: "string",
						enum: [
							"per_night",
							"per_stay",
							"per_person",
							"per_person_per_night",
							"fixed",
						],
					},
					period: {
						type: ["object", "null"],
						additionalProperties: false,
						properties: {
							start: { type: ["string", "null"] },
							end: { type: ["string", "null"] },
						},
						required: ["start", "end"],
					},
					age_range: { type: ["string", "null"] },
					notes: { type: ["string", "null"] },
				},
				required: [
					"kind",
					"applies_to",
					"value_native",
					"value_pct",
					"currency",
					"basis",
					"period",
					"age_range",
					"notes",
				],
			},
		},
		agent_metadata: {
			type: "object",
			additionalProperties: false,
			properties: {
				confidence_score: { type: "number", minimum: 0, maximum: 1 },
				reasoning: { type: "string" },
			},
			required: ["confidence_score", "reasoning"],
		},
	},
	required: [
		"extraction_id",
		"extracted_at",
		"accommodation_lines",
		"accommodation_supplements",
		"agent_metadata",
	],
}

export const ACCOMMODATION_FIELD_SCHEMA_EXAMPLE = {
	extraction_id: "b3e2a8f1-4f88-4d22-9a3c-7d2b1e6a0c91",
	extracted_at: "2026-05-13T16:42:00Z",
	accommodation_lines: [
		{
			room_type_label: "Double Standard Garden View",
			room_category: "Double",
			board_basis: "BB",
			occupancy: 2,
			period: { start: "2026-04-01", end: "2026-06-30" },
			month_set: [4, 5, 6],
			min_stay_nights: 2,
			max_stay_nights: null,
			price_native: 215,
			currency: "EUR",
			price_basis: "per_room_per_night",
			view_or_category: "Garden View",
			notes: null,
		},
		{
			room_type_label: "Double Standard Garden View",
			room_category: "Double",
			board_basis: "HB",
			occupancy: 2,
			period: { start: "2026-04-01", end: "2026-06-30" },
			month_set: [4, 5, 6],
			min_stay_nights: 2,
			max_stay_nights: null,
			price_native: 265,
			currency: "EUR",
			price_basis: "per_room_per_night",
			view_or_category: "Garden View",
			notes: null,
		},
		{
			room_type_label: "Junior Suite Sea View",
			room_category: "Suite",
			board_basis: "BB",
			occupancy: 2,
			period: { start: "2026-04-01", end: "2026-06-30" },
			month_set: [4, 5, 6],
			min_stay_nights: 3,
			max_stay_nights: null,
			price_native: 380,
			currency: "EUR",
			price_basis: "per_room_per_night",
			view_or_category: "Sea View",
			notes: null,
		},
	],
	accommodation_supplements: [
		{
			kind: "single_supplement",
			applies_to: ["Double Standard Garden View", "Junior Suite Sea View"],
			value_native: 80,
			value_pct: null,
			currency: "EUR",
			basis: "per_night",
			period: null,
			age_range: null,
			notes: null,
		},
		{
			kind: "child_rate",
			applies_to: ["Double Standard Garden View"],
			value_native: null,
			value_pct: 50,
			currency: null,
			basis: "per_person_per_night",
			period: null,
			age_range: "2-11",
			notes: "Sharing parents' room.",
		},
		{
			kind: "city_tax",
			applies_to: [],
			value_native: 2,
			value_pct: null,
			currency: "EUR",
			basis: "per_person_per_night",
			period: null,
			age_range: null,
			notes: "Algarve municipal tourism tax.",
		},
	],
	agent_metadata: {
		confidence_score: 0.91,
		reasoning:
			"Emitted 3 lines × 1 period for 2 room types and 2 board bases (BB/HB) plus 3 supplements (single supp, child rate, city tax). All values lifted verbatim from the headline rate table on page 2; board-basis mapping is straightforward (BB/HB labelled in English). City tax appears once at the bottom of the doc and applies to all room types — captured with empty applies_to[].",
	},
}
